const REDACTED = 'REDACTED'
const MASKED_VALUE = '***'

/**
 * Query params we keep: navigation and pagination state that carries no patient data.
 * Everything else is masked rather than denylisted, because table filters put arbitrary
 * columns straight into the query string (mrn[0], patients.display_name[0], dob[0], ...),
 * so there is no fixed set of keys to exclude. Auth0's `code`/`state` are masked by this too.
 */
const PRESERVED_QUERY_KEYS = [
  'division_id',
  'page',
  'per_page',
  'menu_id',
  'item_id',
  'filter_id',
  'tab'
]

// uuid first, so a numeric prefix cannot partially match one; the lookahead keeps
// the match to a whole path segment
const ID_SEGMENT = '(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\\d+)(?=/|$)'

/**
 * Path segments identifying a patient or a monitoring session. Matching only id-shaped
 * segments leaves literal routes (/patients/add, /patient-monitors/multipause) readable.
 * The guest route is matched on any segment because that param is an access token.
 */
const PATH_RULES = [
  [new RegExp(`(/patients)/${ID_SEGMENT}`, 'gi'), `$1/${REDACTED}`],
  // both spellings: the SPA routes use patient-monitors, the API uses patient_monitors
  [new RegExp(`(/patient[-_]monitors)/${ID_SEGMENT}`, 'gi'), `$1/${REDACTED}`],
  [new RegExp(`(/digital-whiteboard/patient)/${ID_SEGMENT}`, 'gi'), `$1/${REDACTED}`],
  [new RegExp(`(/digital-sign/patient)/${ID_SEGMENT}`, 'gi'), `$1/${REDACTED}`],
  [new RegExp(`(/family-note)/${ID_SEGMENT}`, 'gi'), `$1/${REDACTED}`],
  [new RegExp(`(/care-connect)/${ID_SEGMENT}`, 'gi'), `$1/${REDACTED}`],
  [/(\/guest)\/[^/?#]+/gi, `$1/${REDACTED}`]
]

// Only used to parse relative inputs; never returned, since those are rebuilt relative.
const PARSE_BASE = 'http://placeholder.invalid'

/**
 * Removes PHI from a URL before it is sent to a third party (LogRocket, Sentry).
 * Redacts patient and patient monitor ids from the path, and masks every query param
 * value outside PRESERVED_QUERY_KEYS — patient names, MRNs and DOBs reach the URL as
 * table filter values on listing pages.
 *
 * @param {string} urlString - absolute or relative URL
 * @returns {string} the masked URL, relative if the input was relative
 */
export const maskUrl = (urlString) => {
  try {
    if (!urlString || typeof urlString !== 'string') {
      return urlString
    }

    const isRelative = !urlString.startsWith('http')
    if (isRelative && !urlString.startsWith('/')) {
      return urlString
    }
    const url = new URL(urlString, PARSE_BASE)

    url.pathname = PATH_RULES.reduce(
      (path, [pattern, replacement]) => path.replace(pattern, replacement),
      url.pathname
    )

    if (url.search) {
      const masked = new URLSearchParams()
      for (const [key, value] of new URLSearchParams(url.search).entries()) {
        masked.append(key, PRESERVED_QUERY_KEYS.includes(key) ? value : MASKED_VALUE)
      }
      url.search = masked.toString()
    }

    return isRelative ? `${url.pathname}${url.search}${url.hash}` : url.toString()
  } catch {
    return urlString
  }
}
